import { FC, InputHTMLAttributes, useEffect } from 'react';
import { useRouter } from 'next/router';
import * as Yup from 'yup';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { useSignInApi } from './use-sign-in-api';
import { useAuthContext } from '@/context';
import { Form as FormBootstrap, Col } from 'react-bootstrap';
import { useEmpresaList } from './use-empresas-list-api';

const loginSchema = Yup.object().shape({
  login: Yup.string().min(3, 'Minimo 3 caracteres').max(50, 'Maximo 50 caracteres').required('Login é obrigatório'),
  senha: Yup.string().min(3, 'Minimo 3 caracteres').max(50, 'Maximo 50 caracteres').required('Senha é obrigatória'),
  empresaId: Yup.string().required('Empresa é obrigatório')
});

export const Form: FC = () => {
  const { isSignedIn } = useAuthContext();
  const { handleSubmit, touched, errors, getFieldProps, isSubmitting, isValid, status } = useLoginForm();

  const { request, response: responseEmpresa, isLoading } = useEmpresaList();

  useEffect(() => {
    request();
  }, [request]);

  if (isSignedIn) {
    return null;
  }

  return (
    <form className="form w-100" onSubmit={handleSubmit} noValidate>
      {status && <Status status={status} />}

      <div className="input-group has-validation">
        <FormBootstrap.Group as={Col} md={12} xl={12} xxl={12} className="p-1">
          <FormBootstrap.Select {...getFieldProps('empresaId')} className="text-capitalize" disabled={isLoading} style={{ backgroundColor: '#e8f0fe' }}>
            {responseEmpresa?.data?.map((item, index) => (
              <option key={index} value={`${item.id}`}>
                {item?.legalName} ({item?.document.replace(/\D/g, '').replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')})
              </option>
            ))}
          </FormBootstrap.Select>
          <FormBootstrap.Control.Feedback type="invalid">{errors.empresaId}</FormBootstrap.Control.Feedback>
        </FormBootstrap.Group>
        {touched.empresaId && errors.empresaId && <FormBootstrap.Control.Feedback type="invalid">{errors.empresaId}</FormBootstrap.Control.Feedback>}
      </div>

      <div className="input-group has-validation">
        <Input placeholder="Login" autoComplete="username" className={classnames('form-control', touched.login && { 'is-invalid': errors.login, 'is-valid': !errors.login })} {...getFieldProps('login')} />
        {touched.login && errors.login && <div className="invalid-feedback">{errors.login}</div>}
      </div>

      <div className="input-group has-validation">
        <Input placeholder="Senha" type="password" autoComplete="current-password" className={classnames('form-control', touched.senha && { 'is-invalid': errors.senha, 'is-valid': !errors.senha })} {...getFieldProps('senha')} />
        {touched.senha && errors.senha && <div className="invalid-feedback">{errors.senha}</div>}
      </div>
      <SubmitButton isSubmitting={isSubmitting} isValid={isValid} />
    </form>
  );
};

function useLoginForm() {
  const router = useRouter();

  const { token, signIn } = useAuthContext();

  const { setStatus, values, setSubmitting, ...form } = useFormik({
    initialValues: {
      login: '',
      senha: '',
      empresaId: 1
    },
    validationSchema: loginSchema,
    onSubmit() {
      request();
    }
  });
  const { request, response, isLoading, error } = useSignInApi(values.login, values.senha, values.empresaId);

  if (response && response.data) {
    localStorage.setItem('cID', String(response?.data.empresa.id));
    localStorage.setItem('cFantasyName', String(response?.data.empresa.fantasyName));
    localStorage.setItem('cDocument', String(response?.data.empresa.document));
    document.cookie = `cID=${response?.data.empresa.id}; path=/; max-age=86400`;
  }

  useEffect(() => {
    if (token) {
      router.push('/');
    }
  }, [token, router]);

  useEffect(() => {
    if (error) {
      setStatus('Senha ou usuário incorreto. Por favor tente novamente.');
    }
  }, [error, setStatus]);

  useEffect(() => {
    if (response) {
      signIn({ ...response.data });
    }
  }, [response, signIn]);

  useEffect(() => {
    setSubmitting(isLoading);
  }, [setSubmitting, isLoading]);

  return form;
}

export const Input: FC<InputHTMLAttributes<HTMLInputElement>> = props => {
  const style = {
    height: '46px',
    border: 'none',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    borderRadius: '0.5rem',
    marginTop: '1.5rem',
    background: '#e8f0fe'
  };

  return <input style={style} {...props} />;
};

const SubmitButton: FC<{ isSubmitting?: boolean; isValid?: boolean }> = ({ isSubmitting, isValid }) => {
  return (
    <div className="text-center">
      <button type="submit" className="btn btn-lg btn-primary" style={{ borderRadius: '0.5rem', marginTop: '20px', background: '#08213E', borderColor: ' #08213E' }} disabled={isSubmitting || !isValid}>
        {isSubmitting ? (
          <span className="indicator-progress d-block">
            Aguarde...
            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
          </span>
        ) : (
          <span className="indicator-label">Entrar</span>
        )}
      </button>
    </div>
  );
};

const Status: FC<{ status: string }> = ({ status }) => {
  return (
    <div className="mb-lg-15 alert alert-danger">
      <div className="alert-text font-weight-bold">{status}</div>
    </div>
  );
};
